// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { List, ListItem, ListItemButton, Popover } from '@mui/material';
import { AppIcon, Input } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { IlliquidLinkedProductContent } from './illiquid-linked-product-content';
import styles from './illiquid-asset-linked-accounts-section.module.css';
import {
  useIlliquidAssetAccountProductSelector,
  type UseIlliquidAssetAccountProductSelectorProps,
} from './use-illiquid-asset-account-product-selector';

export type { UseIlliquidAssetAccountProductSelectorProps };

export const IlliquidAssetAccountProductSelector = defineComponent({
  displayName: 'IlliquidAssetAccountProductSelector',
  useSetup: useIlliquidAssetAccountProductSelector,
  View: ({
    t,
    inputRef,
    query,
    open,
    anchorEl,
    filtered,
    availableProducts,
    onOpen,
    onClose,
    onChangeQuery,
    onSelectProduct,
  }) => (
    <div className={styles['search-wrap']}>
      <div className="relative">
        <span className={styles['search-icon']}>
          <AppIcon icon="Search" color="disabled" size="base" />
        </span>

        <Input
          ref={inputRef}
          type="text"
          autoComplete="off"
          className={styles['search-input']}
          value={query}
          onChange={onChangeQuery}
          onClick={onOpen}
          onFocus={onOpen}
          placeholder={t(
            'illiquid-asset-drawer.fields.linked-accounts-placeholder',
            { ns: 'components' },
          )}
          data-testid="illiquid-link-account-input"
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            className: styles['popover-paper'],
            style: { width: anchorEl?.clientWidth },
          },
        }}
      >
        <List dense disablePadding>
          {filtered.length === 0 ? (
            <ListItem className={styles['empty-option']}>
              <Translation
                i18nKey={
                  availableProducts.length === 0
                    ? 'illiquid-asset-drawer.fields.linked-accounts-empty'
                    : 'illiquid-asset-drawer.fields.linked-accounts-no-results'
                }
                ns="components"
              />
            </ListItem>
          ) : (
            filtered.map(product => (
              <ListItem key={product.iban} disablePadding>
                <ListItemButton onClick={() => onSelectProduct(product.iban)}>
                  <IlliquidLinkedProductContent
                    product={product}
                    t={t}
                    surface="page"
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Popover>
    </div>
  ),
});
